import { useCallback, useState } from 'react'
import { callApi } from 'utils/api/callApi'
import { FP_ENDPOINTS } from 'apps/fleet-planner/config'
import * as Types from 'apps/fleet-planner/types'

interface IUseShareApiState {
  isLoading: boolean
  shipmentShares: Types.IShare[]
  shareIdDeleting?: string
  error?: string
  toastMessage?: string
  pushNewToastMessage: (message?: string) => void
  resetError: () => void
  getShares: (shipmentId: string) => Promise<any>
  getAndSetShares: (shipmentId: string) => Promise<void>
  createShare: (shipmentId: string, driverId: string) => Promise<any>
  deleteShare: (shipmentId: string, shareId: string) => Promise<any>
  sendShare: (
    shipmentId: string,
    shareId: string,
    shareSend: Types.ISendShareRequest['shareSend'],
    driverId?: string,
    assignDriver?: boolean
  ) => Promise<any>
}

/**
 * @name useShareApi
 * @see https://fp-staging.transfix.io/api-docs/index.html
 */
export const useShareApi = (): IUseShareApiState => {
  const [error, setError] = useState<string>('')
  const [isLoading, setLoading] = useState<boolean>(false)
  const [shareIdDeleting, setShareIdDeleting] = useState<string | undefined>()
  const [shipmentShares, setShipmentShares] = useState<Types.IShare[]>([])

  const [toastMessage, setToastMessage] = useState<string | undefined>()

  /**
   * Display the Toast element and replace the message
   */
  const pushNewToastMessage = useCallback((message?: string) => {
    setToastMessage(message)
  }, [])

  const resetError = useCallback(() => {
    setError('')
  }, [])

  const getShares = useCallback(async (shipmentId: string): Promise<Types.IShare[]> => {
    const { shares } = await callApi({
      method: 'GET',
      endpoint: `${FP_ENDPOINTS.shipments}/${shipmentId}/shares`,
    })
    return shares
  }, [])

  const getAndSetShares = useCallback(
    async (shipmentId: string) => {
      try {
        const shares = await getShares(shipmentId)
        setShipmentShares(shares)
        setError('')
      } catch (e) {
        setError(e)
      }
    },
    [getShares]
  )

  const createShare = useCallback(
    async (shipmentId: string, driverId: string) => {
      setLoading(true)
      try {
        const { share } = await callApi({
          method: 'POST',
          endpoint: `${FP_ENDPOINTS.shipments}/${shipmentId}/shares`,
          body: {
            sharedWithDriverId: driverId,
          },
        })

        // Refresh shares
        await getAndSetShares(shipmentId)
        setLoading(false)
        setError('')
        return share
      } catch (e) {
        setError(e)
        setLoading(false)
        return undefined
      }
    },
    [getAndSetShares]
  )

  const deleteShare = useCallback(
    async (shipmentId: string, shareId: string): Promise<void> => {
      setShareIdDeleting(shareId)
      try {
        await callApi({
          method: 'DELETE',
          endpoint: `${FP_ENDPOINTS.shipments}/${shipmentId}/shares/${shareId}`,
        })

        // Refresh shares
        await getAndSetShares(shipmentId)
        setShareIdDeleting(undefined)
        setError('')
      } catch (e) {
        setError(e)
        setShareIdDeleting(undefined)
      }
    },
    [getAndSetShares]
  )

  const sendShare = useCallback(
    async (
      shipmentId: string,
      shareId: string,
      shareSend: Types.ISendShareRequest['shareSend'],
      driverId?: string,
      assignDriver?: boolean
    ) => {
      setLoading(true)

      try {
        const { share } = await callApi({
          method: 'POST',
          endpoint: `${FP_ENDPOINTS.shipments}/${shipmentId}/shares/${shareId}/send`,
          body: {
            shareSend,
            ...(driverId ? { driverId, assignDriver } : {}),
          },
        })

        // Refresh shares
        await getAndSetShares(shipmentId)
        setLoading(false)
        return share
      } catch (e) {
        setLoading(false)
        setError(
          'Your message wasn’t sent. Your driver may have unsubscribed from text notifications. Ask them to text START to (917) 444 - 9105 to resubscribe.'
        )
        return undefined
      }
    },
    [getAndSetShares]
  )

  return {
    getShares,
    getAndSetShares,
    createShare,
    shareIdDeleting,
    deleteShare,
    sendShare,
    shipmentShares,
    isLoading,
    error,
    resetError,
    toastMessage,
    pushNewToastMessage,
  }
}
